import {
  cloneElement,
  Fragment,
  type Key,
  type ReactElement,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { Check, ChevronDown, LoaderCircle } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { inputStyle } from './input';

export type SearchSelectProps<V> = {
  value?: V;
  placeholder: string;
  options: V[];

  onSelect: (value: V) => void;
  renderValue: (value: V) => string;
  renderOption?: (option: V) => ReactNode;
  optionKey: (option: V) => Key;
  searchBy: (option: V, query: string) => boolean;
  limit?: number;

  disabled?: boolean;
  loading?: boolean;
  error?: boolean;

  className?: string;
  additionalAction?: {
    onClick: () => void;
    label: string;
    leftIcon?: ReactElement<{ className?: string }>;
  };
};

export function SearchSelect<V>({
  value,
  placeholder,
  options,

  onSelect,
  renderValue,
  renderOption = renderValue,
  optionKey,
  searchBy,
  limit,

  disabled = false,
  loading = false,
  error = false,

  className,
  additionalAction,
}: SearchSelectProps<V>) {
  const style = searchSelectStyle({
    loading,
    error,
    disabled,
    withRight: true,
  });
  const [inputValue, setInputValue] = useState<string>('');

  const filteredOptions = useMemo(
    function () {
      if (!inputValue) {
        return options;
      }

      return options
        .filter((option) => searchBy(option, inputValue))
        .slice(0, limit);
    },
    [options, inputValue, searchBy, limit],
  );

  return (
    <Combobox<V>
      // @ts-expect-error - need to fallback on null to keep the field controlled
      value={value ?? null}
      by={(a, b) => a && b && optionKey(a) === optionKey(b)}
      onChange={onSelect}
      disabled={disabled || loading}
    >
      <div className={style.container()}>
        <Combobox.Input<V>
          className={style.input({ className })}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          displayValue={(value) => renderValue(value)}
          placeholder={placeholder}
        />
        <Combobox.Button
          className={style.inputRight({ className: 'pointer-events-auto' })}
        >
          <ChevronDown className={style.inputIcon()} aria-hidden="true" />
        </Combobox.Button>
        <div className={style.inputLoader()}>
          <LoaderCircle
            className={style.inputLoaderIcon()}
            aria-hidden="true"
          />
        </div>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Combobox.Options className={style.options()}>
            {filteredOptions.length === 0 ? (
              <div className={style.optionsNoResult()}>No results</div>
            ) : null}
            {filteredOptions.map((option) => (
              <Combobox.Option
                key={optionKey(option)}
                className={style.option()}
                value={option}
              >
                <span className={style.optionValue()}>
                  {renderOption(option)}
                </span>
                <span className={style.optionSelected()}>
                  <Check
                    className={style.optionSelectedIcon()}
                    aria-hidden="true"
                  />
                </span>
              </Combobox.Option>
            ))}
            {additionalAction ? (
              <div
                className={style.action()}
                onClick={additionalAction.onClick}
              >
                {additionalAction.leftIcon
                  ? cloneElement(additionalAction.leftIcon, {
                      className: style.actionIcon({
                        className: additionalAction.leftIcon.props.className,
                      }),
                    })
                  : null}
                <span className={style.optionValue()}>
                  {additionalAction.label}
                </span>
              </div>
            ) : null}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}

export const searchSelectStyle = tv({
  extend: inputStyle,
  slots: {
    options: `
      absolute
      z-10
      mt-1
      max-h-60
      w-full
      overflow-auto
      rounded-md
      bg-white
      py-1
      text-sm
      shadow-lg
      ring-1
      ring-black/5
      focus:outline-none
    `,
    option: `
      relative
      cursor-default
      select-none
      py-2
      pl-3
      pr-9
      text-neutral-700
      hover:bg-primary-50
      hover:text-primary-800
      ui-active:bg-primary-50
      ui-active:text-primary-800
    `,
    action: `
      relative
      flex
      cursor-pointer
      select-none
      flex-row
      py-2
      pl-3
      pr-9
      text-neutral-500
      hover:bg-primary-50
      hover:text-primary-800
    `,
    actionIcon: `
      mr-2
      size-4
    `,
    optionValue: `
      block
      truncate
      font-normal
      ui-selected:font-semibold
    `,
    optionSelected: `
      absolute
      inset-y-0
      right-0
      hidden
      items-center
      pr-4
      text-primary-600
      ui-selected:flex
      ui-active:text-white
    `,
    optionSelectedIcon: `size-4`,
    optionsNoResult: `
      w-full
      py-2
      text-center
      text-neutral-400
    `,
  },
});
